const tmpCanvas = new OffscreenCanvas(1, 1)
const tmpCtx = tmpCanvas.getContext('2d', { willReadFrequently: true })
tmpCtx.imageSmoothingEnabled = false

export class AtlasRenderer {
  constructor(texture, loadImage) {
    this.texture = texture
    this.loadImage = loadImage || loadImageDefault
    this.pending = new Set()
    this.mipLevels = 4 // 1 + Math.floor(Math.log2(Math.max(width, height)))
  }

  init(width, height, pad) {
    this.width = width
    this.height = height
    this.pad = pad

    this.canvases = new Array(this.mipLevels)
    this.ctxs = new Array(this.mipLevels)

    for (let l = 0; l < this.mipLevels; l++) {
      const w = Math.max(1, width >> l)
      const h = Math.max(1, height >> l)
      const c = new OffscreenCanvas(w, h)
      const cx = c.getContext('2d', { willReadFrequently: false })
      cx.imageSmoothingEnabled = false
      cx.clearRect(0, 0, w, h)
      this.canvases[l] = c
      this.ctxs[l] = cx
    }

    this.maxSlices = 1024
    this.lookupArray = new Float32Array(4 * this.maxSlices)

    this.texture?.update({
      width: this.width,
      height: this.height,
      pad: this.pad,
      maxSlices: this.maxSlices,
      lookupArray: this.lookupArray,
      canvases: this.canvases,
    })
  }

  async draw(id, url, sx, sy, sw, sh, dx, dy, pad, u0, v0, du, dv) {
    const promise = this.loadImage(url)
    this.pending.add(promise)
    let source
    try {
      source = await promise
    } catch (err) {
      console.error(`AtlasRenderer: could not load ${url}`)
      console.error(err)
      this.pending.delete(promise)
      return
    }
    this.pending.delete(promise)

    // console.time('AtlasRenderer.draw')

    // build the tile's mip chain once
    const chain = buildTileMipChain(source, sx, sy, sw, sh, this.mipLevels)

    for (let l = 0; l < this.mipLevels; l++) {
      const atlasCtx = this.ctxs[l]
      const scale = 1 / (1 << l)

      // if the chain ended early (tiny tiles), clamp to last available level
      const lvl = l < chain.length ? chain[l] : chain[chain.length - 1]
      // const l_sw = lvl.w
      // const l_sh = lvl.h
      // const l_dx = Math.round(dx * scale)
      // const l_dy = Math.round(dy * scale)
      // const l_pad = Math.round(pad * scale)

      // Use bit shift to match how mip chain sizes are calculated
      const l_sw = Math.max(1, sw >> l)
      const l_sh = Math.max(1, sh >> l)
      const l_dx = dx >> l // floor via bit shift
      const l_dy = dy >> l
      const l_pad = pad >> l

      // --- Build a temp canvas containing JUST this level's tile pixels
      const TW = l_sw + 2 * l_pad
      const TH = l_sh + 2 * l_pad
      if (tmpCanvas.width !== TW || tmpCanvas.height !== TH) {
        tmpCanvas.width = TW
        tmpCanvas.height = TH
        tmpCtx.imageSmoothingEnabled = false
      }
      tmpCtx.clearRect(0, 0, TW, TH)

      // write tile pixels into the center (l_pad, l_pad)
      const imgData = new ImageData(new Uint8ClampedArray(lvl.data), l_sw, l_sh)
      tmpCtx.putImageData(imgData, l_pad, l_pad)

      if (l_pad > 0) {
        // --- Extrude gutters FROM THE TEMP TILE (never from the atlas)
        // left/right strips
        tmpCtx.drawImage(tmpCanvas, l_pad, l_pad, 1, l_sh, 0, l_pad, l_pad, l_sh)
        tmpCtx.drawImage(tmpCanvas, l_pad + l_sw - 1, l_pad, 1, l_sh, l_pad + l_sw, l_pad, l_pad, l_sh)
        // top/bottom strips
        tmpCtx.drawImage(tmpCanvas, l_pad, l_pad, l_sw, 1, l_pad, 0, l_sw, l_pad)
        tmpCtx.drawImage(tmpCanvas, l_pad, l_pad + l_sh - 1, l_sw, 1, l_pad, l_pad + l_sh, l_sw, l_pad)
        // corners
        tmpCtx.drawImage(tmpCanvas, l_pad, l_pad, 1, 1, 0, 0, l_pad, l_pad) // TL
        tmpCtx.drawImage(tmpCanvas, l_pad + l_sw - 1, l_pad, 1, 1, l_pad + l_sw, 0, l_pad, l_pad) // TR
        tmpCtx.drawImage(tmpCanvas, l_pad, l_pad + l_sh - 1, 1, 1, 0, l_pad + l_sh, l_pad, l_pad) // BL
        tmpCtx.drawImage(tmpCanvas, l_pad + l_sw - 1, l_pad + l_sh - 1, 1, 1, l_pad + l_sw, l_pad + l_sh, l_pad, l_pad) // BR

        // blit (tile + gutters) into the atlas
        atlasCtx.drawImage(tmpCanvas, 0, 0, TW, TH, l_dx - l_pad, l_dy - l_pad, TW, TH)
      } else {
        // no padding case: just blit tile center
        atlasCtx.drawImage(tmpCanvas, l_pad, l_pad, l_sw, l_sh, l_dx, l_dy, l_sw, l_sh)
      }
    }

    // write padded rect to lookup (unchanged from your code)
    // const u0_p = (dx - pad) / this.width
    // const du_p = (sw + 2 * pad) / this.width
    // const v0_p = 1 - (dy - pad + (sh + 2 * pad)) / this.height
    // const dv_p = (sh + 2 * pad) / this.height

    // Store in IMAGE SPACE (no flip)
    const u0_p = (dx - pad) / this.width
    const v0_p = (dy - pad) / this.height
    const du_p = (sw + 2 * pad) / this.width
    const dv_p = (sh + 2 * pad) / this.height

    this.lookupArray[id * 4 + 0] = u0_p
    this.lookupArray[id * 4 + 1] = v0_p
    this.lookupArray[id * 4 + 2] = du_p
    this.lookupArray[id * 4 + 3] = dv_p

    this.texture?.update({
      width: this.width,
      height: this.height,
      pad: this.pad,
      maxSlices: this.maxSlices,
      lookupArray: this.lookupArray,
      canvases: this.canvases,
    })

    this.onChange?.()

    // console.timeEnd('AtlasRenderer.draw')
  }

  async serialize() {
    // wait for any pending draws
    const promises = Array.from(this.pending)
    await Promise.allSettled(promises)
    // create bitmaps
    const bitmaps = await Promise.all(
      this.canvases.map((canvas, idx) => {
        // this.ctxs[idx].fillStyle = 'red'
        // this.ctxs[idx].fillRect(0, this.height / 2, this.width, this.height)
        return createImageBitmap(canvas)
      })
    )

    return {
      width: this.width,
      height: this.height,
      pad: this.pad,
      maxSlices: this.maxSlices,
      lookupArray: this.lookupArray,
      bitmaps,
    }
  }

  resize(width, height) {
    this.width = width
    this.height = height
    for (let l = 0; l < this.mipLevels; l++) {
      const w = Math.max(1, width >> l)
      const h = Math.max(1, height >> l)
      const canvas = this.canvases[l]
      const ctx = this.ctxs[l]
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      canvas.width = w
      canvas.height = h
      ctx.putImageData(imageData, 0, 0)
    }
  }

  clear() {
    for (let l = 0; l < this.canvases.length; l++) {
      const canvas = this.canvases[l]
      const ctx = this.ctxs[l]
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
  }
}

const cache = {}
// let delay = 1
async function loadImageDefault(url) {
  // const ms = delay * 1000
  // delay++
  // await new Promise(resolve => setTimeout(resolve, ms))
  let item = cache[url]
  if (!item) {
    item = {}
    item.source = null
    item.promise = new Promise(async (resolve, reject) => {
      try {
        // const url = filename.startsWith('blob:') ? filename : `/${filename}`
        // const url = filename // always blob or absolute url
        const resp = await fetch(url, { mode: 'cors', cache: 'force-cache' })
        const blob = await resp.blob()
        const source = await createImageBitmap(blob)
        item.source = source
        resolve()
      } catch (err) {
        reject(err)
      }
    })
    cache[url] = item
  }
  if (!item.source) {
    await item.promise
  }
  return item.source
}

// Build next mip level by 2x2 box average with *periodic wrap*
function nextMipBoxPeriodic(srcRGBA, w, h) {
  const dstW = Math.max(1, w >> 1)
  const dstH = Math.max(1, h >> 1)
  const dst = new Uint8ClampedArray(dstW * dstH * 4)

  const needWrapX = (w & (w - 1)) !== 0
  const needWrapY = (h & (h - 1)) !== 0

  const wrap = (n, m) => ((n % m) + m) % m
  const wrapPow2 = (n, m) => n & (m - 1)

  let p = 0
  for (let j = 0; j < dstH; j++) {
    const rawY0 = j * 2
    const y0 = needWrapY ? wrap(rawY0, h) : rawY0 < h ? rawY0 : rawY0 - h
    const y1 = needWrapY ? wrap(y0 + 1, h) : y0 + 1 < h ? y0 + 1 : 0

    const row0 = y0 * w
    const row1 = y1 * w

    for (let i = 0; i < dstW; i++) {
      const rawX0 = i * 2
      const x0 = needWrapX ? wrap(rawX0, w) : rawX0 < w ? rawX0 : rawX0 - w
      const x1 = needWrapX ? wrap(x0 + 1, w) : x0 + 1 < w ? x0 + 1 : 0

      const a = (row0 + x0) << 2
      const b = (row0 + x1) << 2
      const c = (row1 + x0) << 2
      const d = (row1 + x1) << 2

      dst[p] = (srcRGBA[a] + srcRGBA[b] + srcRGBA[c] + srcRGBA[d]) >> 2
      dst[p + 1] = (srcRGBA[a + 1] + srcRGBA[b + 1] + srcRGBA[c + 1] + srcRGBA[d + 1]) >> 2
      dst[p + 2] = (srcRGBA[a + 2] + srcRGBA[b + 2] + srcRGBA[c + 2] + srcRGBA[d + 2]) >> 2
      dst[p + 3] = (srcRGBA[a + 3] + srcRGBA[b + 3] + srcRGBA[c + 3] + srcRGBA[d + 3]) >> 2
      p += 4
    }
  }

  return { data: dst, w: dstW, h: dstH }
}

// Grab pixels of the sub-rect (sx,sy,sw,sh) from an ImageBitmap
function readSubrectRGBA(source, sx, sy, sw, sh) {
  tmpCanvas.width = sw
  tmpCanvas.height = sh
  tmpCtx.imageSmoothingEnabled = false
  tmpCtx.clearRect(0, 0, sw, sh)
  tmpCtx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh)
  return tmpCtx.getImageData(0, 0, sw, sh).data
}

// Build an array of mip levels (level 0..levels-1) for the tile
function buildTileMipChain(source, sx, sy, sw, sh, levels) {
  const chain = []
  // level 0 from the exact sub-rect
  let w = sw,
    h = sh
  let rgba = readSubrectRGBA(source, sx, sy, sw, sh)
  chain.push({ data: rgba, w, h })

  // subsequent levels via periodic box downsample
  for (let l = 1; l < levels; l++) {
    const nxt = nextMipBoxPeriodic(rgba, w, h)
    chain.push(nxt)
    rgba = nxt.data
    w = nxt.w
    h = nxt.h
    if (w === 1 && h === 1 && l + 1 < levels) {
      // optional: stop early if we've hit 1x1
      break
    }
  }
  return chain
}
